/**
 * Comprime una imagen antes de subirla
 * @param {File} file - Archivo de imagen a comprimir
 * @param {number} maxSizeMB - Tamaño máximo en MB (default: 1)
 * @param {number} maxWidthOrHeight - Ancho o alto máximo en píxeles (default: 1920)
 * @returns {Promise<File>} - Archivo comprimido
 */
export const compressImage = async (file, maxSizeMB = 1, maxWidthOrHeight = 1920) => {
  return new Promise((resolve, reject) => {
    // Si el archivo ya es pequeño, no comprimir
    if (file.size <= maxSizeMB * 1024 * 1024) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Redimensionar si es necesario
        if (width > maxWidthOrHeight || height > maxWidthOrHeight) {
          if (width > height) {
            height = (height * maxWidthOrHeight) / width;
            width = maxWidthOrHeight;
          } else {
            width = (width * maxWidthOrHeight) / height;
            height = maxWidthOrHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Convertir a blob con calidad ajustable
        let quality = 0.9;
        const compress = () => {
          canvas.toBlob(
            (blob) => {
              if (blob.size <= maxSizeMB * 1024 * 1024 || quality <= 0.1) {
                const compressedFile = new File([blob], file.name, {
                  type: file.type,
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              } else {
                quality -= 0.1;
                compress();
              }
            },
            file.type,
            quality
          );
        };
        compress();
      };
      img.onerror = reject;
    };
    reader.onerror = reject;
  });
};

/**
 * Comprime un archivo (imagen o PDF)
 * Para PDFs, solo valida el tamaño máximo
 * @param {File} file - Archivo a comprimir
 * @param {number} maxSizeMB - Tamaño máximo en MB (default: 2 para PDFs, 1 para imágenes)
 * @returns {Promise<File>} - Archivo comprimido o original
 */
export const compressFile = async (file, maxSizeMB = null) => {
  const fileType = file.type;
  
  // Si es PDF, validar tamaño máximo (no se puede comprimir fácilmente en el navegador)
  if (fileType === 'application/pdf') {
    const maxSize = maxSizeMB || 5; // 5MB máximo para PDFs
    if (file.size > maxSize * 1024 * 1024) {
      throw new Error(`El archivo PDF es demasiado grande. Tamaño máximo: ${maxSize}MB`);
    }
    return file;
  }

  // Si es imagen, comprimir
  if (fileType.startsWith('image/')) {
    return await compressImage(file, maxSizeMB || 1);
  }

  // Otros tipos de archivo, devolver sin comprimir
  return file;
};

